import { useEffect, useMemo } from 'react';
import { isNoScopeBasedConfig, isScopeBasedConfig } from '../../../types/AccessCheckConfig';
import { AccessCheckAccessInfoContext } from '../../../contexts/AccessCheckAccessInfoContext';
import { useAccessChecks } from '../../../hooks/useAccessChecks';
import { buildAccessInfoFromAccessCheckEvaluations, buildNoScopeAccessInfo } from '../../../utils/determineAccessInfo';
import { buildAccessInfoFromScopeChecks } from '../../../utils/scopeCheck';
import { DEFAULT_HIDE_ON_LOADING } from '../../../constants/defaultHideOnLoading';

/**
 * Props for the AccessInfoRetriever component
 */
import { jsx as _jsx } from "react/jsx-runtime";
function ScopeInfoRetriever({
  config,
  onComplete,
  children
}) {
  const {
    scopesToCheck,
    requireAnyScope = false,
    scopesRequiredForVisibility = [],
    additionalCheck
  } = config;
  const accessInfo = useMemo(() => {
    return buildAccessInfoFromScopeChecks({
      scopesToCheck,
      requireAnyScope,
      scopesRequiredForVisibility,
      additionalCheck
    });
  }, [scopesToCheck, requireAnyScope, scopesRequiredForVisibility, additionalCheck]);
  useEffect(() => {
    if (onComplete) {
      onComplete(accessInfo.status);
    }
  }, [accessInfo.status, onComplete]);
  return /*#__PURE__*/_jsx(AccessCheckAccessInfoContext.Provider, {
    value: accessInfo,
    children: children
  });
}
function NoScopeInfoRetriever({
  config,
  onComplete,
  children
}) {
  const {
    hasAccess,
    isVisible = true
  } = config;
  const accessInfo = useMemo(() => buildNoScopeAccessInfo(hasAccess, isVisible), [hasAccess, isVisible]);
  useEffect(() => {
    if (onComplete) {
      onComplete(accessInfo.status);
    }
  }, [accessInfo.status, onComplete]);
  return /*#__PURE__*/_jsx(AccessCheckAccessInfoContext.Provider, {
    value: accessInfo,
    children: children
  });
}
function ResourceInfoRetriever({
  config,
  onComplete,
  children
}) {
  const {
    resourcesToCheck,
    resourcesRequiredForVisibility = [],
    hideOnLoading = DEFAULT_HIDE_ON_LOADING
  } = config;

  // Combine all resources into a single list for batched checking
  const allResourceInfoList = useMemo(() => [...resourcesToCheck, ...resourcesRequiredForVisibility], [resourcesToCheck, resourcesRequiredForVisibility]);

  // Fetch access check results for all resources in a single batch
  const {
    accessCheckEvals,
    isLoading
  } = useAccessChecks(allResourceInfoList);

  // Compute aggregated access information from all batch results
  const accessInfo = useMemo(() => {
    return buildAccessInfoFromAccessCheckEvaluations({
      resourcesToCheck,
      resourcesRequiredForVisibility,
      accessCheckEvals,
      hideOnLoading,
      isLoading
    });
  }, [accessCheckEvals, isLoading, resourcesToCheck, resourcesRequiredForVisibility, hideOnLoading]);

  // Notify parent component when access check completes
  useEffect(() => {
    if (onComplete) {
      onComplete(accessInfo.status);
    }
  }, [accessInfo.status, onComplete]);

  // Provide access information to all children via context
  return /*#__PURE__*/_jsx(AccessCheckAccessInfoContext.Provider, {
    value: accessInfo,
    children: children
  });
}

/**
 * Component that performs batched access checks for multiple resources and provides
 * the aggregated access information to its children via context.
 *
 * This component combines access checks for required resources with optional minimum
 * visibility requirements, determines overall access status, and manages loading states.
 *
 * Routes to either scope-based or resource-based retrieval based on the config mode.
 */
export function AccessInfoRetriever({
  config,
  children
}) {
  if (isScopeBasedConfig(config)) {
    return /*#__PURE__*/_jsx(ScopeInfoRetriever, {
      config: config,
      onComplete: config.onComplete,
      children: children
    });
  }
  if (isNoScopeBasedConfig(config)) {
    return /*#__PURE__*/_jsx(NoScopeInfoRetriever, {
      config: config,
      onComplete: config.onComplete,
      children: children
    });
  }
  return /*#__PURE__*/_jsx(ResourceInfoRetriever, {
    config: config,
    onComplete: config.onComplete,
    children: children
  });
}